import { PERMISSIONS, type Permission } from './permissions.js';
import { SITE_ADMIN_TABS } from './site-admin-navigation.js';

export type RoutePolicy =
  | { type: 'permission'; permission: Permission }
  | { type: 'anyOf'; permissions: Permission[] }
  | { type: 'adminHub' };

export interface RoutePolicyRule {
  pattern: string;
  policy: RoutePolicy;
}

export interface RoutePolicyContext {
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (...permissions: Permission[]) => boolean;
  canAccessAdmin: boolean;
}

function normalizePath(path: string): string {
  const trimmed = path.replace(/\/$/, '');
  return trimmed || '/';
}

/** Coincide rutas estáticas y con parámetros (`:id`) sin depender de react-router. */
export function matchRoutePattern(pattern: string, pathname: string): boolean {
  const patternNorm = normalizePath(pattern);
  const pathNorm = normalizePath(pathname);

  if (patternNorm === pathNorm) return true;

  const patternParts = patternNorm === '/' ? [] : patternNorm.slice(1).split('/');
  const pathParts = pathNorm === '/' ? [] : pathNorm.slice(1).split('/');

  if (patternParts.length !== pathParts.length) return false;

  return patternParts.every((part, index) => part.startsWith(':') || part === pathParts[index]);
}

const ROUTE_POLICY_EXTRAS: RoutePolicyRule[] = [
  { pattern: '/dashboard', policy: { type: 'permission', permission: PERMISSIONS.DASHBOARD_ACCESS } },
  { pattern: '/administracion', policy: { type: 'adminHub' } },
  { pattern: '/profile', policy: { type: 'permission', permission: PERMISSIONS.PERFIL_VER } },

  { pattern: '/categorias/create', policy: { type: 'permission', permission: PERMISSIONS.CATEGORIAS_CREAR } },
  { pattern: '/categorias/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.CATEGORIAS_EDITAR } },

  { pattern: '/courses/create', policy: { type: 'permission', permission: PERMISSIONS.CURSOS_CREAR } },
  { pattern: '/courses/:id/builder', policy: { type: 'permission', permission: PERMISSIONS.CONSTRUCTOR_VER } },
  {
    pattern: '/courses/:id/edit',
    policy: { type: 'anyOf', permissions: [PERMISSIONS.CURSOS_EDITAR, PERMISSIONS.CURSOS_SUPERVISAR] },
  },
  { pattern: '/courses/:id/preview', policy: { type: 'permission', permission: PERMISSIONS.CURSOS_VER } },
  { pattern: '/courses/:id/foro/:threadId', policy: { type: 'permission', permission: PERMISSIONS.CURSOS_VER } },
  { pattern: '/courses/:id/foro', policy: { type: 'permission', permission: PERMISSIONS.CURSOS_VER } },
  { pattern: '/courses/:id/horas-sesion', policy: { type: 'permission', permission: PERMISSIONS.CURSOS_VER } },
  {
    pattern: '/courses/:id/play/:item',
    policy: { type: 'anyOf', permissions: [PERMISSIONS.CURSOS_VER, PERMISSIONS.MIS_CURSOS_VER] },
  },
  { pattern: '/courses/:id', policy: { type: 'permission', permission: PERMISSIONS.CURSOS_VER } },

  { pattern: '/library/create', policy: { type: 'permission', permission: PERMISSIONS.BIBLIOTECA_CREAR } },
  { pattern: '/library/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.BIBLIOTECA_EDITAR } },
  { pattern: '/library/:id/preview', policy: { type: 'permission', permission: PERMISSIONS.BIBLIOTECA_VER } },
  { pattern: '/library/:id', policy: { type: 'permission', permission: PERMISSIONS.BIBLIOTECA_VER } },

  { pattern: '/media/:id', policy: { type: 'permission', permission: PERMISSIONS.MULTIMEDIA_VER } },

  { pattern: '/h5p/constructor/:id', policy: { type: 'permission', permission: PERMISSIONS.H5P_EDITAR } },
  { pattern: '/h5p/constructor', policy: { type: 'permission', permission: PERMISSIONS.H5P_EDITAR } },
  { pattern: '/h5p/create', policy: { type: 'permission', permission: PERMISSIONS.H5P_IMPORTAR } },
  { pattern: '/h5p/libraries/:libraryId', policy: { type: 'permission', permission: PERMISSIONS.H5P_DEPENDENCIAS_VER } },
  { pattern: '/h5p/libraries', policy: { type: 'permission', permission: PERMISSIONS.H5P_DEPENDENCIAS_VER } },
  { pattern: '/h5p/librerias', policy: { type: 'permission', permission: PERMISSIONS.H5P_DEPENDENCIAS_VER } },
  { pattern: '/h5p/:id/preview', policy: { type: 'permission', permission: PERMISSIONS.H5P_VER } },
  { pattern: '/h5p/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.H5P_EDITAR } },
  { pattern: '/h5p/:id', policy: { type: 'permission', permission: PERMISSIONS.H5P_VER } },

  { pattern: '/evaluaciones/create', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_CREAR } },
  {
    pattern: '/evaluaciones/intentos/:attemptId',
    policy: { type: 'anyOf', permissions: [PERMISSIONS.EVALUACIONES_VER, PERMISSIONS.MIS_CURSOS_VER] },
  },
  { pattern: '/evaluaciones/:id/preview', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_VER } },
  { pattern: '/evaluaciones/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_EDITAR } },
  { pattern: '/evaluaciones/:id', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_VER } },

  { pattern: '/examen-practico/nuevo', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_CREAR } },
  { pattern: '/examen-practico/vehiculos/nuevo', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_CREAR } },
  { pattern: '/examen-practico/vehiculos/:id', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_VER } },
  { pattern: '/examen-practico/vehiculos', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_VER } },
  { pattern: '/examen-practico/:id', policy: { type: 'permission', permission: PERMISSIONS.EVALUACIONES_VER } },

  { pattern: '/preguntas/create', policy: { type: 'permission', permission: PERMISSIONS.PREGUNTAS_CREAR } },
  { pattern: '/preguntas/bancos', policy: { type: 'permission', permission: PERMISSIONS.PREGUNTAS_VER } },
  { pattern: '/preguntas/importar', policy: { type: 'permission', permission: PERMISSIONS.PREGUNTAS_CREAR } },
  { pattern: '/preguntas/importar/xhtml', policy: { type: 'permission', permission: PERMISSIONS.PREGUNTAS_CREAR } },

  { pattern: '/calificaciones/curso/:courseId', policy: { type: 'permission', permission: PERMISSIONS.CALIFICACIONES_VER } },

  {
    pattern: '/progreso/configuracion-sesion',
    policy: { type: 'permission', permission: PERMISSIONS.PROGRESO_ADMINISTRAR },
  },
  {
    pattern: '/progreso/tiempo-conexion/:userId',
    policy: {
      type: 'anyOf',
      permissions: [PERMISSIONS.PROGRESO_TIEMPO_VER, PERMISSIONS.PROGRESO_ADMINISTRAR],
    },
  },
  {
    pattern: '/progreso/tiempo-conexion',
    policy: {
      type: 'anyOf',
      permissions: [PERMISSIONS.PROGRESO_TIEMPO_VER, PERMISSIONS.PROGRESO_ADMINISTRAR],
    },
  },
  {
    pattern: '/progreso/:userId/:courseId',
    policy: { type: 'anyOf', permissions: [PERMISSIONS.PROGRESO_ADMINISTRAR, PERMISSIONS.PROGRESO_VER] },
  },

  {
    pattern: '/expediente/admin/:userId',
    policy: {
      type: 'anyOf',
      permissions: [PERMISSIONS.EXPEDIENTE_VER_TODOS, PERMISSIONS.EXPEDIENTE_ADMINISTRAR],
    },
  },
  {
    pattern: '/expediente/admin',
    policy: {
      type: 'anyOf',
      permissions: [PERMISSIONS.EXPEDIENTE_VER_TODOS, PERMISSIONS.EXPEDIENTE_ADMINISTRAR],
    },
  },

  { pattern: '/inscripciones/bulk', policy: { type: 'permission', permission: PERMISSIONS.INSCRIPCIONES_ASIGNAR } },
  { pattern: '/inscripciones/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.INSCRIPCIONES_ADMINISTRAR } },
  { pattern: '/inscripciones/:id', policy: { type: 'permission', permission: PERMISSIONS.INSCRIPCIONES_VER } },

  { pattern: '/grupos/create', policy: { type: 'permission', permission: PERMISSIONS.GRUPOS_ADMINISTRAR } },
  { pattern: '/grupos/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.GRUPOS_ADMINISTRAR } },
  { pattern: '/grupos/:id', policy: { type: 'permission', permission: PERMISSIONS.GRUPOS_VER } },

  { pattern: '/credenciales/emitir', policy: { type: 'permission', permission: PERMISSIONS.CREDENCIALES_EMITIR } },
  { pattern: '/credenciales/plantillas', policy: { type: 'permission', permission: PERMISSIONS.CREDENCIALES_PLANTILLAS } },
  { pattern: '/credenciales/plantillas/:id/editor', policy: { type: 'permission', permission: PERMISSIONS.CREDENCIALES_PLANTILLAS } },
  { pattern: '/credenciales/plantillas/:id', policy: { type: 'permission', permission: PERMISSIONS.CREDENCIALES_PLANTILLAS } },
  { pattern: '/credenciales/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.CREDENCIALES_ADMINISTRAR } },
  { pattern: '/credenciales/:id', policy: { type: 'permission', permission: PERMISSIONS.CREDENCIALES_VER } },

  { pattern: '/contratos/plantillas/:id/editor', policy: { type: 'permission', permission: PERMISSIONS.CONTRATOS_PLANTILLAS } },
  { pattern: '/contratos/plantillas', policy: { type: 'permission', permission: PERMISSIONS.CONTRATOS_PLANTILLAS } },
  { pattern: '/contratos/:id', policy: { type: 'permission', permission: PERMISSIONS.CONTRATOS_VER } },

  { pattern: '/politicas-legales/:id/editor', policy: { type: 'permission', permission: PERMISSIONS.POLITICAS_LEGALES_ADMINISTRAR } },
  { pattern: '/politicas-legales/aceptaciones', policy: { type: 'permission', permission: PERMISSIONS.POLITICAS_LEGALES_VER } },
  { pattern: '/politicas-legales', policy: { type: 'permission', permission: PERMISSIONS.POLITICAS_LEGALES_VER } },

  { pattern: '/apariencia', policy: { type: 'permission', permission: PERMISSIONS.APARIENCIA_VER } },

  { pattern: '/aceptar-politicas', policy: { type: 'permission', permission: PERMISSIONS.DASHBOARD_ACCESS } },

  { pattern: '/mis-contratos/:id', policy: { type: 'permission', permission: PERMISSIONS.MIS_CONTRATOS_VER } },

  { pattern: '/soporte/admin/:id', policy: { type: 'permission', permission: PERMISSIONS.SOPORTE_VER } },
  { pattern: '/soporte/admin', policy: { type: 'permission', permission: PERMISSIONS.SOPORTE_VER } },
  { pattern: '/soporte/:id', policy: { type: 'permission', permission: PERMISSIONS.MIS_SOPORTE_VER } },
  { pattern: '/soporte', policy: { type: 'permission', permission: PERMISSIONS.MIS_SOPORTE_VER } },

  { pattern: '/usuarios/create', policy: { type: 'permission', permission: PERMISSIONS.USUARIOS_CREAR } },
  { pattern: '/usuarios/importar', policy: { type: 'permission', permission: PERMISSIONS.USUARIOS_CREAR } },
  { pattern: '/usuarios/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.USUARIOS_EDITAR } },
  {
    pattern: '/usuarios/:id',
    policy: {
      type: 'anyOf',
      permissions: [PERMISSIONS.USUARIOS_VER, PERMISSIONS.ESTUDIANTES_PERFIL_VER],
    },
  },

  { pattern: '/roles/create', policy: { type: 'permission', permission: PERMISSIONS.ROLES_CREAR } },
  { pattern: '/roles/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.ROLES_EDITAR } },
  { pattern: '/roles/:id', policy: { type: 'permission', permission: PERMISSIONS.ROLES_VER } },

  { pattern: '/campos-personalizados/preview', policy: { type: 'permission', permission: PERMISSIONS.CAMPOS_USUARIO_VER } },
  { pattern: '/campos-personalizados/orden', policy: { type: 'permission', permission: PERMISSIONS.CAMPOS_USUARIO_EDITAR } },
  { pattern: '/campos-personalizados/create', policy: { type: 'permission', permission: PERMISSIONS.CAMPOS_USUARIO_CREAR } },
  { pattern: '/campos-personalizados/grupos/create', policy: { type: 'permission', permission: PERMISSIONS.CAMPOS_USUARIO_CREAR } },
  { pattern: '/campos-personalizados/grupos/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.CAMPOS_USUARIO_EDITAR } },
  { pattern: '/campos-personalizados/grupos', policy: { type: 'permission', permission: PERMISSIONS.CAMPOS_USUARIO_VER } },
  { pattern: '/campos-personalizados/:id/edit', policy: { type: 'permission', permission: PERMISSIONS.CAMPOS_USUARIO_EDITAR } },

  { pattern: '/mis-cursos/:id/foro/:threadId', policy: { type: 'permission', permission: PERMISSIONS.MIS_CURSOS_VER } },
  { pattern: '/mis-cursos/:id/foro', policy: { type: 'permission', permission: PERMISSIONS.MIS_CURSOS_VER } },
  { pattern: '/mis-cursos/:id/calificaciones', policy: { type: 'permission', permission: PERMISSIONS.MIS_CURSOS_VER } },
  { pattern: '/mis-cursos/:id/play/:item', policy: { type: 'permission', permission: PERMISSIONS.MIS_CURSOS_VER } },
  { pattern: '/mis-cursos/:id', policy: { type: 'permission', permission: PERMISSIONS.MIS_CURSOS_VER } },
  { pattern: '/calendario', policy: { type: 'permission', permission: PERMISSIONS.MIS_CURSOS_VER } },
  { pattern: '/mis-certificados/insignias', policy: { type: 'permission', permission: PERMISSIONS.MIS_CREDENCIALES_VER } },
];

function collectNavRoutePolicies(): RoutePolicyRule[] {
  const rules: RoutePolicyRule[] = [];
  const seen = new Set<string>();

  for (const tab of Object.values(SITE_ADMIN_TABS)) {
    for (const section of tab.sections) {
      for (const link of section.links) {
        if (!link.permission || seen.has(link.to)) continue;
        seen.add(link.to);
        rules.push({
          pattern: link.to,
          policy: { type: 'permission', permission: link.permission },
        });
      }
    }
  }

  return rules;
}

/** Reglas más específicas primero; luego las del menú de administración. */
export const ROUTE_POLICIES: RoutePolicyRule[] = [...ROUTE_POLICY_EXTRAS, ...collectNavRoutePolicies()];

export function resolveRoutePolicy(pathname: string): RoutePolicy | null {
  for (const rule of ROUTE_POLICIES) {
    if (matchRoutePattern(rule.pattern, pathname)) {
      return rule.policy;
    }
  }
  return null;
}

export function isRoutePolicyAllowed(policy: RoutePolicy, ctx: RoutePolicyContext): boolean {
  switch (policy.type) {
    case 'permission':
      return ctx.hasPermission(policy.permission);
    case 'anyOf':
      return ctx.hasAnyPermission(...policy.permissions);
    case 'adminHub':
      return ctx.canAccessAdmin;
    default:
      return false;
  }
}

/**
 * Rutas autenticadas sin política de permiso en `ROUTE_POLICIES`.
 * - `/admin/*`: `AdminLegacyRedirect` resuelve y redirige a rutas con política.
 * - `/mis-credenciales*`: alias legacy hacia `/mis-certificados`.
 */
export function isRouteGuardException(pathname: string): boolean {
  const normalized = normalizePath(pathname);

  if (normalized === '/') {
    return true;
  }

  if (normalized === '/mis-credenciales' || normalized.startsWith('/mis-credenciales/')) {
    return true;
  }

  if (normalized === '/admin' || normalized.startsWith('/admin/')) {
    return true;
  }

  return false;
}
